import { type ReturnUseApprovalsListProps, type IApprovalTab, type ReturnUseApprovalsListApiProps, type UseApprovalsListApiProps, type QueryParamsProps } from 'modules/cbre-approvals-widget/types';
import { useState, useEffect, useRef } from 'react';
import service from 'util/apiService';

type MyApprovalsFilter = {
  name: string;
  value: string[];
};

type MyApprovalsRequestPayload = {
  search: string;
  searchfields: string;
  pageSize: number;
  pageNumber: number;
  order: 'asc' | 'desc' | 'ASC' | 'DESC';
  filters: MyApprovalsFilter[];
} & Record<string, unknown>;

const normalizeFilters = (filters?: QueryParamsProps['filters']): MyApprovalsFilter[] => {
  if (!filters) {
    return [];
  }

  if (Array.isArray(filters)) {
    return filters;
  }

  return filters
    .split('|')
    .map((filter) => filter.trim())
    .filter(Boolean)
    .map((filter) => {
      const separator = filter.includes(':') ? ':' : '=';
      const [name, rawValue = ''] = filter.split(separator);
      const valueSeparator = rawValue.includes('$$') ? '$$' : ',';

      return {
        name,
        value: rawValue
          .split(valueSeparator)
          .map((value) => decodeURIComponent(value.trim()))
          .filter(Boolean),
      };
    })
    .filter(({ name }) => Boolean(name));
};

const useApprovalsListApi = <T>({
  url: baseUrl,
  queryParams = {},
  payload = null,
}: UseApprovalsListApiProps<T>): ReturnUseApprovalsListApiProps<T> => {
  const [approvals, setApprovals] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const resolvePending  = useRef<{resolve: null | ((value: ReturnUseApprovalsListProps | PromiseLike<ReturnUseApprovalsListProps>) => void)}>({resolve: null}).current;
  const renderMethod = useRef({method : null}).current
  const [approvalMethod, setApprovalMethod] = useState<IApprovalTab["tab"]["value"]>("myApprovals");
  const [totalPageCount, setTotalPageCount] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [url, setUrl] = useState<string>(() => {
    // Ensure the baseUrl ends with a '/'
    return baseUrl.endsWith('/')
      ? `${baseUrl}approvals`
      : `${baseUrl}/approvals`;
  });
  const [params, setQueryParams] = useState<Partial<QueryParamsProps>>(() => {
    if (!queryParams.pageSize)
      queryParams.pageSize = 10;
    if (!queryParams.pageNumber)
      queryParams.pageNumber = 1;
    return queryParams;
  });
  const [body, setPayload] = useState<T | null>(payload);
  const hasMounted = useRef({isInitial: true}).current;
  const [pageIndex, setPageIndex] = useState((queryParams?.pageNumber || 1) - 1);
  const [pageSize] = useState(queryParams.pageSize || 10);

 
  
  const fetchMyApprovals = async (withExistenceData?: boolean) => {
    try {
      const requestUrl = baseUrl.endsWith('/')
        ? `${baseUrl}approvals/my-approvals`
        : `${baseUrl}/approvals/my-approvals`;
      const bodyPayload = body && typeof body === 'object' && !Array.isArray(body)
        ? body as Record<string, unknown>
        : {};
      const mergedParams = {
        ...params,
        ...bodyPayload,
      } as Partial<MyApprovalsRequestPayload>;
      const requestPayload: MyApprovalsRequestPayload = {
        search: mergedParams.search || '',
        searchfields: mergedParams.searchfields || 'requestId,serialNumber,assetId',
        pageSize: Number(mergedParams.pageSize || 20),
        pageNumber: Number(mergedParams.pageNumber || 1),
        order: mergedParams.order || 'desc',
        ...mergedParams,
        filters: normalizeFilters(mergedParams.filters),
      };
      !withExistenceData && setLoading(true)
      const response = await service.post(requestUrl, requestPayload);
      setTotalPageCount(response.data.pageCount)
      setTotalRecords(response.data.totalRecords)
      setApprovals((prev) => {
        if(withExistenceData) return [...prev, ...response.data?.recordsList || []];
        return response.data?.recordsList || []
    });
      setLoading(false)
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false)
    }
  };

  const fetchExternalApprovals = async (withExistenceData?: boolean) => {
    try {
      const requestUrl = baseUrl.endsWith('/')
      ? `${baseUrl}approvals/external-approval`
      : `${baseUrl}/approvals/external-approval`;
      const bodyPayload = body && typeof body === 'object' && !Array.isArray(body)
        ? body as Record<string, unknown>
        : {};
      const mergedParams = {
        ...params,
        ...bodyPayload,
      } as Partial<MyApprovalsRequestPayload>;
      const requestPayload: MyApprovalsRequestPayload = {
        search: mergedParams.search || '',
        searchfields: mergedParams.searchfields || 'requestId,serialNumber,assetId',
        pageSize: Number(mergedParams.pageSize || 20),
        pageNumber: Number(mergedParams.pageNumber || 1),
        order: mergedParams.order || 'desc',
        ...mergedParams,
        filters: normalizeFilters(mergedParams.filters),
      };
      !withExistenceData && setLoading(true)
      const response = await service.post(requestUrl, requestPayload);
      setTotalPageCount(response.data.pageCount)
      setTotalRecords(response.data.totalRecords)
      setApprovals((prev) => {
        if(withExistenceData) return [...prev, ...response.data?.recordsList || []];
        return response.data?.recordsList || []
    });
      setLoading(false)
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false)
    }
  };

  const fetchExternalApprovalsHistory = async (withExistenceData?: boolean) => {
    try {
      const requestUrl = baseUrl.endsWith('/')
          ? `${baseUrl}approvals/external-approval/history`
          : `${baseUrl}/approvals/external-approval/history`;
      const bodyPayload = body && typeof body === 'object' && !Array.isArray(body)
        ? body as Record<string, unknown>
        : {};
      const mergedParams = {
        ...params,
        ...bodyPayload,
      } as Partial<MyApprovalsRequestPayload>;
      const requestPayload: MyApprovalsRequestPayload = {
        search: mergedParams.search || '',
        searchfields: mergedParams.searchfields || 'requestId,serialNumber,assetId',
        pageSize: Number(mergedParams.pageSize || 20),
        pageNumber: Number(mergedParams.pageNumber || 1),
        order: mergedParams.order || 'desc',
        ...mergedParams,
        filters: normalizeFilters(mergedParams.filters),
      };
      !withExistenceData && setLoading(true)
      const response = await service.post(requestUrl, requestPayload);
      setTotalPageCount(response.data.pageCount)
      setTotalRecords(response.data.totalRecords)
      setApprovals((prev) => {
        if(withExistenceData) return [...prev, ...response.data?.recordsList || []];
        return response.data?.recordsList || []
      });
      setLoading(false)
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false)
    }
  };

  const fetchApprovalsHistory = async (withExistenceData?: boolean) => {
    try {
      const requestUrl = baseUrl.endsWith('/')
      ? `${baseUrl}approvals/history`
      : `${baseUrl}/approvals/history`;
      const bodyPayload = body && typeof body === 'object' && !Array.isArray(body)
        ? body as Record<string, unknown>
        : {};
      const mergedParams = {
        ...params,
        ...bodyPayload,
      } as Partial<MyApprovalsRequestPayload>;
      const requestPayload: MyApprovalsRequestPayload = {
        search: mergedParams.search || '',
        searchfields: mergedParams.searchfields || 'requestId,serialNumber,assetId',
        pageSize: Number(mergedParams.pageSize || 20),
        pageNumber: Number(mergedParams.pageNumber || 1),
        order: mergedParams.order || 'desc',
        ...mergedParams,
        filters: normalizeFilters(mergedParams.filters),
      };
      !withExistenceData && setLoading(true)
      const response = await service.post(requestUrl, requestPayload);
      setTotalPageCount(response.data.pageCount)
      setTotalRecords(response.data.totalRecords)
      setApprovals((prev) => {
        if(withExistenceData) return [...prev, ...response.data?.recordsList || []];
        return response.data?.recordsList || []
    });
      setLoading(false)
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false)
    }
  };



  const fetchApprovals = async (approvalType?: IApprovalTab["tab"]["value"], withExistenceData?: boolean ) => {
    const currentApprovalType = approvalType || approvalMethod;
    try {
      if(currentApprovalType){
        if(currentApprovalType === 'myApprovals') {
           fetchMyApprovals(withExistenceData)
        }
        if(currentApprovalType === 'externalApprovals') {
           fetchExternalApprovals(withExistenceData)
        }
        if(currentApprovalType === 'externalApprovalsHistory') {
          fetchExternalApprovalsHistory(withExistenceData)
        }
        if(currentApprovalType === 'approvalsHistory') {
          fetchApprovalsHistory(withExistenceData)
        }
      }
     
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handlePendingChanges = (): Promise<ReturnUseApprovalsListProps> => {
    return new Promise<ReturnUseApprovalsListProps>((resolve) => {
      resolvePending.resolve = resolve;
    });
  }


  const returnType = {
    approvals,
    url,
    pageSize,
    loading,
    params,
    totalPageCount,
    totalRecords,
    initialQueryParams: queryParams,
    setUrl,
    setQueryParams,
    setPayload,
    setPageIndex,
    setApprovals,
    fetchApprovals,
    fetchMyApprovals,
    fetchExternalApprovals,
    fetchApprovalsHistory,
    setApprovalMethod,
    handlePendingChanges
  }

  // useEffect(()=>{
  //   if(hasMounted.isInitial) {hasMounted.isInitial = false; return;}
  //   fetchApprovals()
  // }, [pageIndex])

  useEffect(()=>{
    const config = {...returnType, handlePendingChanges: undefined}
    resolvePending?.resolve && resolvePending.resolve({ ...config });
  }, [approvalMethod, pageIndex, pageSize, url, params, body])

  return returnType
};

export default useApprovalsListApi;
